import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Star } from 'lucide-react';

import { Modal } from '@/components/Modal';
import { useTenantAuth } from '@/contexts/TenantAuthContext';
import { narrowDocumentTypes } from '@/lib/documents/narrow-document-types';
import { useActiveCompanyScope } from '@/lib/use-active-company';
import { type DocumentClass, type DocumentType, useDocumentTypes } from '@/lib/use-document-types';
import { useQuickList } from '@/lib/use-quick-list';

interface Props {
  open: boolean;
  docClass: DocumentClass;
  onSelect: (documentTypeId: string, companyId: string) => void;
  onClose: () => void;
  /** Firma dokumentu, z którego modal jest otwierany (np. formatka create) — domyślna wartość filtra firmy. */
  initialCompanyId?: string | null;
  /** Aktualny typ dokumentu, z którego modal jest otwierany — wstępnie zaznaczany wiersz. */
  initialDocumentTypeId?: string | null;
  /** Nadpisanie etykiety przycisku potwierdzenia — domyślnie "Otwórz formularz" (tryb create);
   * D.8 krok 2 (zmiana typu na zapisanym dokumencie) podaje np. "Zmień typ". */
  confirmLabel?: string;
  /**
   * Zawężenie listy do typów dokumentu z zewnętrznego kontekstu (Etap 3, blok C —
   * `useDocumentTypeNarrowing` na liście, z której modal jest otwierany: URL
   * `?documentTypeIds=`/legacy `?documentTypeId=` + filtr stały pozycji menu). Belt-and-suspenders
   * na wierzchu istniejących filtrów tekstowych — „Dodaj" na listach zawsze otwiera modal
   * (bez skrótu pomijającego wybór typu), a lista w modalu pokazuje WYŁĄCZNIE dozwolone typy.
   * Zawężenie obejmuje też lokalne kopie typów globalnych z zawężenia (ten sam kod w klasie —
   * reguła jak na serwerze, patrz `narrowDocumentTypes`). Pusta tablica/`undefined`
   * = brak zawężenia (zachowanie bez zmian).
   */
  allowedTypeIds?: string[];
}

/** Wybrany wiersz — typ + firma, do której należy (unia obejmuje ten sam typ globalny w kilku firmach). */
interface SelectedType {
  id: string;
  companyId: string;
}

export function DocumentTypePickerModal({
  open,
  docClass,
  onSelect,
  onClose,
  initialCompanyId = null,
  initialDocumentTypeId = null,
  confirmLabel,
  allowedTypeIds,
}: Props) {
  const { t } = useTranslation();
  const { me } = useTenantAuth();

  const companies = useMemo(
    () => [...(me?.companies ?? [])].sort((a, b) => a.name.localeCompare(b.name)),
    [me?.companies],
  );

  // Modal zawsze pobiera UNIĘ typów po wszystkich firmach widocznych dla usera
  // (companyId pominięty w query do GET /api/document-types) — niezależnie od trybu
  // globalnego przełącznika firmy w topbarze. Zawężenie do jednej firmy jest lokalnym
  // tekstowym filtrem (`filterCompany` niżej), nie parametrem zapytania — dzięki temu
  // modal może pokazać/zaznaczyć typ firmy dokumentu, z którego został otwarty, nawet gdy
  // różni się od aktywnej firmy w topbarze. Typy globalne są materializowane pod tym samym
  // id per firma, więc klucz wiersza/testid/pin-lookup musi być kompozytowy (id, companyId)
  // — patrz `rowKey` niżej.
  const companyScope = useActiveCompanyScope();

  const {
    data: rawTypes,
    allRows: allTypeRows,
    isLoading,
    isError,
  } = useDocumentTypes(docClass, {
    companyId: null,
  });

  // Wiersz typu bez firmy (relikt sprzed wymogu obowiązkowej firmy przy tworzeniu typu —
  // dziś POST /document-types zawsze wymaga companyId) nie może spełnić inwariantu
  // "utworzenie dokumentu zawsze niesie konkretną firmę" — pomijamy go całkowicie
  // zamiast cicho degradować kontrakt onSelect(documentTypeId, companyId).
  //
  // `allowedTypeIds` (Etap 3, blok C) zawęża dalej do typów dozwolonych przez kontekst
  // zewnętrzny (URL/filtr stały pozycji menu, z której modal został otwarty) — belt-and-
  // -suspenders na wierzchu tego samego filtra. Gdy wynik ma dokładnie jeden wiersz, efekt
  // auto-zaznaczenia niżej (fallback na `types[0]`) sam go preselektuje.
  //
  // Zawężenie idzie TĄ SAMĄ regułą co lista dokumentów na serwerze (`narrowDocumentTypes`):
  // id z zawężenia to zwykle id typów GLOBALNYCH, a firma, która skopiowała typ globalny, ma
  // własną kopię pod INNYM id (ten sam kod) — przesłania ona typ globalny w tej firmie (wiersz
  // globalny jest wtedy niewybieralny i hook go odsiewa), więc samo porównanie id wycinałoby
  // kopię i picker twierdziłby „żaden typ nie pasuje", gdy lista pokazuje dokumenty tej firmy.
  const types = useMemo(() => {
    const withCompany = rawTypes.filter(
      (dt): dt is DocumentType & { companyId: string } => dt.companyId !== null,
    );
    return narrowDocumentTypes(withCompany, allTypeRows, allowedTypeIds ?? []);
  }, [rawTypes, allTypeRows, allowedTypeIds]);

  // Rozróżnienie pustego stanu (blok C, finding [C]): „typy istnieją, ale bieżące
  // zawężenie (URL / filtr stały pozycji menu) wyklucza wszystkie" ≠ „brak typów w ogóle".
  // Prawda tylko gdy `allowedTypeIds` faktycznie coś wyciął (były wiersze przed zawężeniem).
  const narrowingExcludesAll = useMemo(
    () =>
      !!allowedTypeIds &&
      allowedTypeIds.length > 0 &&
      types.length === 0 &&
      rawTypes.some((dt) => dt.companyId !== null),
    [allowedTypeIds, types.length, rawTypes],
  );

  const { pinnedIds, isLoading: isQuickListLoading, pin, unpin } = useQuickList('document_type');

  const [selected, setSelected] = useState<SelectedType | null>(null);
  const [filterCode, setFilterCode] = useState('');
  const [filterName, setFilterName] = useState('');
  const [filterCompany, setFilterCompany] = useState('');
  const [showFullList, setShowFullList] = useState(false);

  useEffect(() => {
    if (!open) {
      setSelected(null);
      setFilterCode('');
      setFilterName('');
      setFilterCompany('');
      setShowFullList(false);
    }
  }, [open]);

  // Domyślna wartość pola "Firma" przy otwarciu: pierwszeństwo ma firma dokumentu, z
  // którego modal jest otwierany (`initialCompanyId`, np. formatka create); bez niej
  // spada na dzisiejsze zachowanie wejścia z listy — aktywną firmę z topbara, gdy user
  // nie jest w trybie "Wszystkie firmy". Puste pole = brak zawężenia (wszystkie firmy).
  useEffect(() => {
    if (open) {
      const defaultCompanyId =
        initialCompanyId ?? (companyScope.mode === 'company' ? companyScope.companyId : null);
      setFilterCompany(companies.find((c) => c.id === defaultCompanyId)?.name ?? '');
    }
  }, [open, initialCompanyId, companyScope.mode, companyScope.companyId, companies]);

  useEffect(() => {
    // `isQuickListLoading` musi zablokować inicjalizację, dopóki `pinnedIds` się nie
    // rozwiąże — inaczej gdy `types` (osobny async query) dojdzie PIERWSZY, ta gałąź
    // widzi jeszcze pusty Set przypięć, wymusza pełną listę, i NIGDY tego nie koryguje
    // (poniższy strażnik `selected !== null` blokuje ponowne przeliczenie po tym, jak
    // przypięcia faktycznie dojdą — TC22b nie łapie tego bo ustawia pinnedIds synchronicznie).
    if (!open || selected !== null || types.length === 0 || isQuickListLoading) return;

    if (initialDocumentTypeId) {
      const match = types.find(
        (dt) =>
          dt.id === initialDocumentTypeId &&
          (initialCompanyId == null || dt.companyId === initialCompanyId),
      );
      if (match) {
        setSelected({ id: match.id, companyId: match.companyId });
        // Wiersz ma być WIDOCZNIE zaznaczony — widok "tylko przypięte" mógłby go ukryć.
        // Wymuszamy pełną listę TYLKO gdy typ nie jest przypięty — jeśli jest, lista
        // podręczna i tak go pokaże, więc zostaje domyślnym trybem (nie wymuszamy bez potrzeby).
        if (!pinnedIds.has(match.id)) {
          setShowFullList(true);
        }
        return;
      }
    }

    const first = types[0];
    if (first) {
      setSelected({ id: first.id, companyId: first.companyId });
    }
  }, [
    open,
    selected,
    types,
    initialDocumentTypeId,
    initialCompanyId,
    pinnedIds,
    isQuickListLoading,
  ]);

  // Klucz wiersza/testid zawsze kompozytowy (id + companyId) — modal pobiera UNIĘ
  // typów po wszystkich firmach, więc typ globalny powtarza się z tym samym id raz
  // per firma; sam `dt.id` nie jest unikalny w unii.
  const rowKey = (dt: DocumentType): string => `${dt.id}:${dt.companyId}`;

  // Baza do liczenia pinów i auto-fallbacku — zawężona do tekstu wpisanego w polu "Firma",
  // NIE cała unia `types`. Inaczej wpisanie firmy bez pinów pokazałoby pusty ekran "tylko
  // przypięte" zamiast fallbacku do pełnej listy (piny z innej firmy nie mają tu znaczenia —
  // liczy się to, co user faktycznie widzi po zawężeniu do firmy).
  const typesInCompanyFilter = types.filter((dt) => {
    const name = companies.find((c) => c.id === dt.companyId)?.name ?? '';
    return name.toLowerCase().includes(filterCompany.toLowerCase());
  });

  // Auto-fallback: gdy user nie ma żadnych przypięć W WYBRANEJ FIRMIE, lista podręczna
  // byłaby pusta — pokazujemy wtedy pełną listę bez dodatkowego kliku (wzorowane na
  // mechanizmie ESITE.WorkFlow — nigdy nie renderujemy pustego stanu, tylko cicho spadamy na
  // pełną listę).
  const pinnedCount = typesInCompanyFilter.filter((dt) => pinnedIds.has(dt.id)).length;
  const effectiveShowFull = showFullList || pinnedCount === 0;

  const filteredTypes = typesInCompanyFilter.filter(
    (dt) =>
      dt.code.toLowerCase().includes(filterCode.toLowerCase()) &&
      dt.name.toLowerCase().includes(filterName.toLowerCase()) &&
      (effectiveShowFull || pinnedIds.has(dt.id)),
  );

  // Zaznaczony wiersz może wypadnąć z listy po zmianie filtra (np. filtra firmy) — confirm
  // musi to blokować, inaczej user zatwierdzi wybór, którego już nie widzi na ekranie.
  const isSelectedVisible =
    selected !== null &&
    filteredTypes.some((dt) => dt.id === selected.id && dt.companyId === selected.companyId);

  const footer = (
    <div className="flex w-full items-center justify-between gap-2">
      <button
        type="button"
        onClick={() => setShowFullList((v) => !v)}
        data-testid="document-type-picker-toggle-full-list"
        className={
          'inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition ' +
          (effectiveShowFull
            ? 'border-accent-500 bg-accent-500/10 text-accent-500'
            : 'border-border-primary bg-bg-primary text-text-secondary hover:border-accent-500 hover:text-accent-500')
        }
      >
        {effectiveShowFull ? (
          <Star className="h-3.5 w-3.5" />
        ) : (
          <Star className="h-3.5 w-3.5" fill="currentColor" />
        )}
        {effectiveShowFull
          ? t('tenant.documents.typePicker.toggleShowPinned')
          : t('tenant.documents.typePicker.toggleShowFull')}
      </button>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onClose}
          className="rounded-lg px-3 py-1.5 text-sm text-text-secondary hover:bg-bg-secondary"
          data-testid="document-type-picker-cancel"
        >
          {t('common.cancel')}
        </button>
        <button
          type="button"
          onClick={() => {
            if (selected) onSelect(selected.id, selected.companyId);
          }}
          disabled={!isSelectedVisible}
          className="rounded-lg bg-accent-600 px-3 py-1.5 text-sm text-white hover:bg-accent-700 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="document-type-picker-confirm"
        >
          {confirmLabel ?? t('tenant.documents.typePicker.confirm')}
        </button>
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('tenant.documents.typePicker.title')}
      size="xl"
      footer={footer}
    >
      {filterCompany.trim() === '' && (
        <p
          className="mb-2 px-1 text-sm text-text-secondary"
          data-testid="document-type-picker-all-mode-hint"
        >
          {t('tenant.documents.typePicker.pickTypeForCompany')}
        </p>
      )}

      {(isLoading || isQuickListLoading) && (
        <p className="px-3 py-2 text-sm text-text-muted">{t('common.loading')}</p>
      )}
      {isError && <p className="px-3 py-2 text-sm text-red-500">{t('common.errorLoading')}</p>}
      {!isLoading && !isQuickListLoading && !isError && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border-primary text-left text-xs font-medium uppercase tracking-wide text-text-tertiary">
              <th className="w-8 pb-2 pl-1 pr-2" />
              <th className="w-8 pb-2 pr-2" />
              <th className="max-w-[8rem] truncate whitespace-nowrap pb-2 pr-4">
                {t('tenant.documentTypes.list.colCode')}
              </th>
              <th className="max-w-[16rem] truncate whitespace-nowrap pb-2 pr-4">
                {t('tenant.documentTypes.list.colName')}
              </th>
              <th className="max-w-[10rem] truncate whitespace-nowrap pb-2 pr-4">
                {t('tenant.documentTypes.list.colCompany')}
              </th>
              <th className="max-w-[16rem] truncate whitespace-nowrap pb-2">
                {t('tenant.documentTypes.list.colDescription')}
              </th>
            </tr>
            <tr className="border-b border-border-primary">
              <th className="pl-1 pr-2" />
              <th className="pr-2" />
              <th className="py-1 pr-4">
                <input
                  type="text"
                  value={filterCode}
                  onChange={(e) => setFilterCode(e.target.value)}
                  placeholder={t('tenant.documents.typePicker.filterCode')}
                  className="w-full rounded border border-border-primary bg-bg-primary px-2 py-1 text-xs font-normal normal-case text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent-600"
                  data-testid="document-type-picker-filter-code"
                />
              </th>
              <th className="py-1 pr-4">
                <input
                  type="text"
                  value={filterName}
                  onChange={(e) => setFilterName(e.target.value)}
                  placeholder={t('tenant.documents.typePicker.filterName')}
                  className="w-full rounded border border-border-primary bg-bg-primary px-2 py-1 text-xs font-normal normal-case text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent-600"
                  data-testid="document-type-picker-filter-name"
                />
              </th>
              <th className="py-1 pr-4">
                <input
                  type="text"
                  value={filterCompany}
                  onChange={(e) => setFilterCompany(e.target.value)}
                  placeholder={t('tenant.documents.typePicker.filterCompany')}
                  className="w-full rounded border border-border-primary bg-bg-primary px-2 py-1 text-xs font-normal normal-case text-text-primary placeholder:text-text-muted focus:outline-none focus:ring-1 focus:ring-accent-600"
                  data-testid="document-type-picker-filter-company"
                />
              </th>
              <th className="py-1" />
            </tr>
          </thead>
          <tbody>
            {filteredTypes.map((dt) => {
              const isPinned = pinnedIds.has(dt.id);
              const key = rowKey(dt);
              const isRowSelected =
                selected !== null && selected.id === dt.id && selected.companyId === dt.companyId;
              const companyName = companies.find((c) => c.id === dt.companyId)?.name ?? '';
              return (
                <tr
                  key={key}
                  className="cursor-pointer hover:bg-bg-secondary"
                  onClick={() => setSelected({ id: dt.id, companyId: dt.companyId })}
                >
                  <td className="py-2 pl-1 pr-2">
                    <input
                      type="radio"
                      name="doc-type-picker"
                      value={key}
                      checked={isRowSelected}
                      onChange={() => setSelected({ id: dt.id, companyId: dt.companyId })}
                      className="accent-accent-600"
                      data-testid={`document-type-picker-option-${key}`}
                    />
                  </td>
                  <td className="py-2 pr-2">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isPinned) {
                          unpin(dt.id);
                        } else {
                          pin(dt.id);
                        }
                      }}
                      title={
                        isPinned
                          ? t('tenant.documents.typePicker.unpinTooltip')
                          : t('tenant.documents.typePicker.pinTooltip')
                      }
                      className={
                        'flex items-center justify-center rounded p-0.5 hover:bg-bg-primary ' +
                        (isPinned ? 'text-amber-500' : 'text-text-tertiary')
                      }
                      data-testid={`document-type-picker-pin-${key}`}
                    >
                      <Star className="h-4 w-4" fill={isPinned ? 'currentColor' : 'none'} />
                    </button>
                  </td>
                  <td className="max-w-[8rem] py-2 pr-4">
                    <div className="flex min-w-0 items-center gap-1.5" title={dt.code}>
                      <span className="truncate whitespace-nowrap font-mono text-text-primary">
                        {dt.code}
                      </span>
                      <span
                        className={
                          'inline-flex shrink-0 items-center rounded-full px-1.5 py-0.5 text-[10px] font-medium ' +
                          (dt.origin === 'global'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'bg-bg-tertiary text-text-secondary')
                        }
                        data-testid={`document-type-picker-origin-${key}`}
                      >
                        {dt.origin === 'global'
                          ? t('tenant.documents.typePicker.origin.system')
                          : t('tenant.documents.typePicker.origin.own')}
                      </span>
                    </div>
                  </td>
                  <td className="py-2 pr-4 text-text-primary">
                    <span
                      className="block max-w-[16rem] truncate whitespace-nowrap"
                      title={dt.name}
                    >
                      {dt.name}
                    </span>
                  </td>
                  <td className="py-2 pr-4 text-text-primary">
                    <span
                      className="block max-w-[10rem] truncate whitespace-nowrap"
                      title={companyName}
                    >
                      {companyName}
                    </span>
                  </td>
                  <td className="py-2 text-text-primary">
                    <span
                      className="block max-w-[16rem] truncate whitespace-nowrap"
                      title={dt.description ?? ''}
                    >
                      {dt.description ?? ''}
                    </span>
                  </td>
                </tr>
              );
            })}
            {filteredTypes.length === 0 && (
              <tr>
                <td colSpan={6} className="py-4 text-center text-text-muted">
                  {types.length === 0
                    ? narrowingExcludesAll
                      ? t('tenant.documents.typePicker.noTypeMatchesNarrowing')
                      : t('tenant.documents.typePicker.noTypesConfigured')
                    : t('common.noResults')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      )}
    </Modal>
  );
}
